import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { IftaLabel } from 'primeng/iftalabel';
import { InputText } from 'primeng/inputtext';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BdExportColumnOption } from './export-column-option';
import { BdExportTemplateColumnsFormComponent } from './export-template-columns-form.component';

export interface BdExportTemplateFormValue {
  name: string;
  propertyIds: string[];
}

/**
 * Presentational export-template form (name + reorderable column picker), shared
 * by the virtual-clinic and clinics-filter features. The catalog is supplied via
 * {@link columns}; a valid `{ name, propertyIds }` value is emitted on every
 * valid change so the host page can build its own domain object. `idPrefix`
 * keeps each feature's existing element ids (e.g. e2e/anchor ids) stable.
 */
@Component({
  selector: 'bd-export-template-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-2">
      <div>
        <p-iftalabel>
          <input
            pInputText
            fluid
            variant="filled"
            id="{{ idPrefix() }}-name"
            [attr.data-test]="idPrefix() + '-name'"
            [formControl]="formGroup.controls.name"
            autocomplete="off"
          />
          <label for="{{ idPrefix() }}-name">Name der Vorlage</label>
        </p-iftalabel>
      </div>
      <div>
        <bd-export-template-columns-form
          [columns]="columns()"
          [idPrefix]="idPrefix()"
          [propertyIds]="formGroup.controls.propertyIds.value"
          (propertyIdsChange)="onPropertyIdsChange($event)"
        ></bd-export-template-columns-form>
      </div>
    </div>
  `,
  standalone: true,
  imports: [
    FormsModule,
    IftaLabel,
    InputText,
    ReactiveFormsModule,
    BdExportTemplateColumnsFormComponent,
  ],
})
export class BdExportTemplateFormComponent {
  public readonly columns = input<BdExportColumnOption[]>([]);
  public readonly name = input<string>('');
  public readonly propertyIds = input<string[]>([]);
  public readonly idPrefix = input<string>('export-template-form');
  public readonly valueChange = output<BdExportTemplateFormValue>();

  public readonly formGroup: FormGroup<{
    name: FormControl<string>;
    rowsLength: FormControl<number>;
    propertyIds: FormControl<string[]>;
  }> = new FormGroup({
    name: new FormControl<string>(null, [Validators.required]),
    rowsLength: new FormControl<number>(0, [Validators.min(1)]),
    propertyIds: new FormControl<string[]>([]),
  });

  constructor() {
    effect(() => {
      const name = this.name();
      const propertyIds = this.propertyIds();
      this.formGroup.patchValue(
        { name, propertyIds, rowsLength: propertyIds.length },
        { emitEvent: false },
      );
    });

    this.formGroup.valueChanges
      .pipe(
        filter(() => this.formGroup.valid),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.valueChange.emit({
          name: this.formGroup.controls.name.value,
          propertyIds: [...this.formGroup.controls.propertyIds.value],
        });
      });
  }

  public onPropertyIdsChange(propertyIds: string[]): void {
    this.formGroup.patchValue({
      propertyIds,
      rowsLength: propertyIds.length,
    });
  }
}
